import type { DayCycle } from './dayCycle';
import { prayerLabel } from './prayerBlocks';
import type { RoutineStep } from '../types';

/**
 * Time-anchoring for routines that contain prayer steps.
 *
 * Routines are normally RELATIVE (elapsed time). A prayer step is ABSOLUTE: its
 * start + duration come from the prayer Settings (resolved per day), not from a
 * typed duration. So when a routine has a prayer step we back-schedule the
 * lead-up steps to finish at the prayer's anchor (Iqamah by default, or Adhan),
 * flow the following steps forward from the prayer's end, and surface a
 * recommended "Start at HH:MM".
 *
 * Anchoring keys off the FIRST resolvable prayer step (its lead-up ends exactly
 * at its anchor). Any later prayer step is a checkpoint: if the forward flow
 * reaches it before its anchor there's idle slack (fine); if it overruns the
 * anchor we flag a conflict ("you may not make it on time").
 */

export interface ScheduledStep {
  stepId: string;
  /** Minutes from midnight. */
  startMin: number;
  endMin: number;
  isPrayer: boolean;
  /** Prayer step that couldn't be resolved for the day (disabled / high latitude). */
  unavailable?: boolean;
  /** Preceding steps overrun this prayer's anchor. */
  conflict?: boolean;
}

export interface RoutineSchedule {
  /** Absolute times per step, in step order. Empty when not time-aware. */
  items: ScheduledStep[];
  /** Recommended clock time to begin (minutes from midnight), or null. */
  recommendedStartMin: number | null;
  warnings: string[];
  /** True when the routine has at least one resolvable prayer anchor. */
  timeAware: boolean;
}

/** The absolute window a prayer step occupies, from the day's resolved prayer times. */
export function prayerStepWindow(
  step: RoutineStep,
  cycle: DayCycle | null,
): { anchorMin: number; endMin: number } | null {
  if (step.type !== 'prayer' || !step.prayerKey || !cycle?.prayers) return null;
  const p = cycle.prayers.find(pr => pr.key === step.prayerKey);
  if (!p) return null;
  const anchorMin = step.anchor === 'adhan' ? p.adhanMinutes : p.iqamahMinutes;
  const endMin = p.endMinutes;
  if (anchorMin == null || endMin == null) return null;
  return { anchorMin, endMin };
}

export function scheduleRoutine(steps: RoutineStep[], cycle: DayCycle | null): RoutineSchedule {
  const windows = steps.map(s => prayerStepWindow(s, cycle));
  const firstPrayerIdx = steps.findIndex((s, i) => s.type === 'prayer' && windows[i] != null);

  if (firstPrayerIdx < 0) {
    return { items: [], recommendedStartMin: null, warnings: [], timeAware: false };
  }

  // Lead time before the first prayer → recommended start ends the lead at the anchor.
  let lead = 0;
  for (let i = 0; i < firstPrayerIdx; i++) {
    const w = windows[i];
    lead += w ? w.endMin - w.anchorMin : steps[i].duration;
  }
  const firstAnchor = windows[firstPrayerIdx]!.anchorMin;
  const recommendedStartMin = firstAnchor - lead;

  const items: ScheduledStep[] = [];
  const warnings: string[] = [];
  let cursor = recommendedStartMin;

  steps.forEach((s, i) => {
    if (s.type === 'prayer') {
      const w = windows[i];
      if (!w) {
        items.push({ stepId: s.id, startMin: cursor, endMin: cursor, isPrayer: true, unavailable: true });
        if (s.prayerKey) warnings.push(`${prayerLabel(s.prayerKey)} isn't available today — it'll be skipped.`);
        return;
      }
      const conflict = cursor > w.anchorMin + 0.5;
      if (conflict && s.prayerKey) {
        warnings.push(
          `The steps before ${prayerLabel(s.prayerKey)} run past its ${s.anchor === 'adhan' ? 'Adhan' : 'Iqamah'} — you may not make it on time.`,
        );
      }
      items.push({ stepId: s.id, startMin: w.anchorMin, endMin: w.endMin, isPrayer: true, conflict });
      cursor = w.endMin;
    } else {
      const start = cursor;
      const end = cursor + s.duration;
      items.push({ stepId: s.id, startMin: start, endMin: end, isPrayer: false });
      cursor = end;
    }
  });

  return { items, recommendedStartMin, warnings, timeAware: true };
}

/** Format minutes-from-midnight as "h:mm AM/PM" (handles wrap to prev/next day). */
export function formatMinOfDay(min: number): string {
  const wrapped = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${period}`;
}
